import { observer } from "mobx-react-lite";
import { useState, useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import { PROMO_BANNER_STATE, PROMO_BANNER_OPT, URL } from "const";
import { useMediaQuery } from "hooks";
import Cookies from "js-cookie";
import ae from "lib/analytics";
import cn from "classnames";

import { Button, Image, Icon } from "./basic";

const BANNER_OPT = Math.round(Math.random());
const BANNER_OPT_NAME = Object.keys(PROMO_BANNER_OPT).find(k => PROMO_BANNER_OPT[k] === BANNER_OPT);

function PromoBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const router = useRouter();

  const handleBannerClose = useCallback(() => {
    Cookies.set("kultura_promo_banner", PROMO_BANNER_STATE.HIDDEN, { expires: 7, path: "/" });
    setShowBanner(false);
  }, []);

  const handleBannerClick = useCallback(() => {
    handleBannerClose(BANNER_OPT_NAME);
    ae.promo.onboarding.bannerClick(BANNER_OPT_NAME);
    router.push({ pathname: URL.SITE_OVERVIEW, query: "ob_banner=true" });
  }, [handleBannerClose, router]);

  useEffect(() => {
    const cookieVal = Cookies.get("kultura_promo_banner");
    if (cookieVal === PROMO_BANNER_STATE.HIDDEN) {
      setShowBanner(false);
      return;
    }

    setTimeout(() => {
      setShowBanner(true);
      ae.promo.onboarding.bannerShow(BANNER_OPT_NAME);
    }, 5000);
  }, []);

  useEffect(() => {
    function scrollHandler() {
      if (Math.ceil(window.innerHeight + window.scrollY) >= document.body.scrollHeight) {
        setIsBottom(true);
        return;
      }

      setIsBottom(false);
    }

    document.addEventListener("scroll", scrollHandler);

    return () => document.removeEventListener("scroll", scrollHandler);
  }, []);

  if (!showBanner) return null;

  if (BANNER_OPT === PROMO_BANNER_OPT.A) {
    return (
      <div className="fixed z-100 h-full w-full animate-appear bg-black-100/80 md:pointer-events-none md:bottom-0 md:bg-transparent">
        <div
          className={cn(
            "pointer-events-auto absolute left-[50%] flex h-[90%] w-full max-w-1340 translate-x-[-50%] bg-cover md:h-[50rem] lg:h-300",
            isBottom ? "md:bottom-[6.8rem] xl:bottom-[5.2rem] 2xl:bottom-[3.2rem]" : "bottom-0"
          )}
          style={{ backgroundImage: `url('/img/promo-banner-a-${isDesktop ? "lg" : "sm"}.webp')` }}
        >
          <Button
            className="absolute right-20 top-[13%] flex h-35 w-35 items-center justify-center rounded-full bg-white-100 md:top-50 lg:top-20"
            onClick={() => {
              handleBannerClose();
              ae.promo.onboarding.bannerClose(BANNER_OPT_NAME);
            }}
            aria-label="close promo banner"
          >
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="18" fill="#fff" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.47 26.89a1.007 1.007 0 0 0 1.418 0l7.262-7.215 7.263 7.215c.392.39 1.027.39 1.42 0l.175-.174a.992.992 0 0 0 0-1.41l-7.264-7.215 7.263-7.215a.992.992 0 0 0 0-1.41l-.176-.174a1.008 1.008 0 0 0-1.418 0l-7.263 7.215-7.261-7.214a1.008 1.008 0 0 0-1.42 0l-.175.174a.992.992 0 0 0 0 1.41l7.262 7.214-7.262 7.214a.992.992 0 0 0 0 1.41l.175.174Z"
                fill="url(#a)"
              />
              <defs>
                <linearGradient id="a" x1="9.534" y1="24.065" x2="26.509" y2="22.569" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#7A4BF1" />
                  <stop offset="1" stopColor="#4E8FFC" />
                </linearGradient>
              </defs>
            </svg>
          </Button>

          <div className="my-auto flex w-full px-50 text-white-100 md:absolute md:bottom-40 md:px-80 lg:bottom-70 lg:px-20 xl:px-55">
            <div>
              <h2 className="mb-20 text-30 font-bold leading-tight">Things you can do on KULTURA - for free</h2>
              <ul className="flex flex-col space-y-40 text-18 md:space-y-20 lg:flex-row lg:space-x-20 lg:space-y-0 lg:text-15 xl:space-x-50 xl:text-20">
                <li className="flex items-center leading-tight">
                  <svg
                    width="41"
                    height="42"
                    viewBox="0 0 41 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-10"
                  >
                    <path
                      d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.5469 36.5 25.0469 35"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M33.478 17.7324V35.5547H30.2432V21.5044L25.9585 22.9204V20.3081L33.1118 17.7324H33.478Z"
                      fill="white"
                    />
                  </svg>
                  Create your own (free)
                  <br /> art collection
                </li>
                <li className="flex items-center leading-tight">
                  <svg
                    width="40"
                    height="42"
                    viewBox="0 0 40 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-10"
                  >
                    <path
                      d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.2909 35.437 20.4632 35.4416"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M37.2134 33.0156V35.5547H25.1772V33.3818L30.9512 27.144C31.5534 26.4604 32.0295 25.8664 32.3794 25.3618C32.7293 24.8573 32.9775 24.4015 33.124 23.9946C33.2786 23.5796 33.356 23.1849 33.356 22.8105C33.356 22.2653 33.2583 21.7892 33.063 21.3823C32.8677 20.9673 32.5828 20.6458 32.2085 20.418C31.8423 20.1901 31.3906 20.0762 30.8535 20.0762C30.2594 20.0762 29.7508 20.2145 29.3276 20.4912C28.9126 20.7598 28.5952 21.1341 28.3755 21.6143C28.1639 22.0944 28.0581 22.6437 28.0581 23.2622H24.8232C24.8232 22.2124 25.0674 21.2562 25.5557 20.3936C26.0439 19.5228 26.7438 18.827 27.6553 18.3062C28.5667 17.7853 29.6491 17.5249 30.9023 17.5249C32.123 17.5249 33.1566 17.7284 34.0029 18.1353C34.8493 18.534 35.4922 19.1037 35.9316 19.8442C36.3711 20.5848 36.5908 21.4678 36.5908 22.4932C36.5908 23.0628 36.4972 23.6243 36.3101 24.1777C36.1229 24.7311 35.8584 25.2804 35.5166 25.8257C35.1829 26.3628 34.7842 26.908 34.3203 27.4614C33.8564 28.0148 33.3397 28.5804 32.77 29.1582L29.3032 33.0156H37.2134Z"
                      fill="white"
                    />
                  </svg>
                  Our AI recommends
                  <br /> art just for you
                </li>
                <li className="flex items-center leading-tight">
                  <svg
                    width="40"
                    height="42"
                    viewBox="0 0 40 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-10"
                  >
                    <path
                      d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.2909 35.437 20.4632 35.4416"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <path
                      d="M28.7051 25.2397H30.5239C31.1668 25.2397 31.6958 25.1299 32.1108 24.9102C32.534 24.6823 32.8473 24.373 33.0508 23.9824C33.2624 23.5837 33.3682 23.1239 33.3682 22.603C33.3682 22.0903 33.2746 21.6468 33.0874 21.2725C32.9002 20.89 32.6154 20.597 32.2329 20.3936C31.8586 20.182 31.3784 20.0762 30.7925 20.0762C30.3205 20.0762 29.8851 20.1698 29.4863 20.3569C29.0957 20.5441 28.7824 20.8127 28.5464 21.1626C28.3185 21.5044 28.2046 21.9154 28.2046 22.3955H24.9697C24.9697 21.4352 25.222 20.5929 25.7266 19.8687C26.2393 19.1362 26.931 18.5625 27.8018 18.1475C28.6725 17.7324 29.645 17.5249 30.7192 17.5249C31.8911 17.5249 32.9165 17.7202 33.7954 18.1108C34.6825 18.4933 35.3701 19.0589 35.8584 19.8076C36.3548 20.5563 36.603 21.484 36.603 22.5908C36.603 23.1198 36.4769 23.6488 36.2246 24.1777C35.9805 24.6986 35.6183 25.1706 35.1382 25.5938C34.6662 26.0088 34.0843 26.3465 33.3926 26.6069C32.709 26.8592 31.9237 26.9854 31.0366 26.9854H28.7051V25.2397ZM28.7051 27.73V26.0088H31.0366C32.0376 26.0088 32.9043 26.1268 33.6367 26.3628C34.3773 26.5907 34.9876 26.9162 35.4678 27.3394C35.9479 27.7544 36.3019 28.2386 36.5298 28.792C36.7658 29.3454 36.8838 29.9395 36.8838 30.5742C36.8838 31.4043 36.7292 32.1449 36.4199 32.7959C36.1107 33.4388 35.6753 33.984 35.1138 34.4316C34.5604 34.8792 33.9093 35.221 33.1606 35.457C32.4119 35.6849 31.6022 35.7988 30.7314 35.7988C29.9746 35.7988 29.2381 35.6971 28.522 35.4937C27.814 35.2821 27.1751 34.9688 26.6055 34.5537C26.0358 34.1305 25.5841 33.6016 25.2505 32.9668C24.9168 32.3239 24.75 31.5793 24.75 30.7329H27.9849C27.9849 31.2212 28.1029 31.6566 28.3389 32.0391C28.583 32.4215 28.9167 32.7227 29.3398 32.9424C29.7712 33.154 30.2554 33.2598 30.7925 33.2598C31.3866 33.2598 31.8952 33.154 32.3184 32.9424C32.7497 32.7227 33.0793 32.4134 33.3071 32.0146C33.535 31.6077 33.6489 31.1235 33.6489 30.562C33.6489 29.8947 33.5228 29.3535 33.2705 28.9385C33.0182 28.5234 32.6561 28.2183 32.1841 28.0229C31.7202 27.8276 31.1668 27.73 30.5239 27.73H28.7051Z"
                      fill="white"
                    />
                  </svg>
                  Connect with fellow
                  <br /> art curious people
                </li>
              </ul>
            </div>
          </div>
          <Button
            className="absolute bottom-70 right-[23rem] rounded-6 bg-white-100 px-35 py-15 font-semibold transition-colors xl:px-40 xl:py-20 xl:text-20"
            onClick={handleBannerClick}
          >
            <span
              className="bg-gradient-to-r from-new-purple-100 to-new-blue-100 bg-clip-text"
              style={{ WebkitTextFillColor: "transparent" }}
            >
              Find out
            </span>
          </Button>
          <Image
            src="/img/promo-banner-asset.webp"
            className="absolute bottom-0 right-0 z-[-1]"
            alt="Explore KULTURA"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed z-100 h-full w-full animate-appear bg-black-100/80 md:right-0 md:w-full md:max-w-[55rem] md:bg-transparent">
      <div
        className="absolute bottom-0 flex h-[87%] w-full flex-col items-start justify-center bg-cover bg-no-repeat px-30 pt-80 leading-tight text-white-100 md:h-[65.5rem] md:pl-[18rem] md:pr-20 md:pt-0"
        style={{ backgroundImage: `url('/img/promo-banner-b-${isDesktop ? "lg" : "sm"}.webp')` }}
      >
        <div className="w-full md:mt-80">
          <Button
            className="group absolute right-20 top-[13%] flex h-35 w-35 items-center justify-center rounded-full transition-colors hover:bg-white-100 md:right-30 md:top-85 2xl:right-55 2xl:top-90"
            onClick={() => {
              handleBannerClose();
              ae.promo.onboarding.bannerClose(BANNER_OPT_NAME);
            }}
            aria-label="close promo banner"
          >
            <Icon icon="cross" className="h-24 w-24 text-white-100 group-hover:hidden" />
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="hidden group-hover:block"
            >
              <circle cx="18" cy="18" r="18" fill="#fff" />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.47 26.89a1.007 1.007 0 0 0 1.418 0l7.262-7.215 7.263 7.215c.392.39 1.027.39 1.42 0l.175-.174a.992.992 0 0 0 0-1.41l-7.264-7.215 7.263-7.215a.992.992 0 0 0 0-1.41l-.176-.174a1.008 1.008 0 0 0-1.418 0l-7.263 7.215-7.261-7.214a1.008 1.008 0 0 0-1.42 0l-.175.174a.992.992 0 0 0 0 1.41l7.262 7.214-7.262 7.214a.992.992 0 0 0 0 1.41l.175.174Z"
                fill="url(#a)"
              />
              <defs>
                <linearGradient id="a" x1="9.534" y1="24.065" x2="26.509" y2="22.569" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#7A4BF1" />
                  <stop offset="1" stopColor="#4E8FFC" />
                </linearGradient>
              </defs>
            </svg>
          </Button>
          <h2 className="text-left text-25 font-bold md:text-35 lg:max-w-[44rem]">
            Things you can do
            <br className="block md:hidden" /> on KULTURA - for free
          </h2>
          <ul className="mt-20 space-y-20 text-20 font-semibold md:space-y-30">
            <li className="flex items-center leading-tight">
              <svg
                width="41"
                height="42"
                viewBox="0 0 41 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-10"
              >
                <path
                  d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.5469 36.5 25.0469 35"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M33.478 17.7324V35.5547H30.2432V21.5044L25.9585 22.9204V20.3081L33.1118 17.7324H33.478Z"
                  fill="white"
                />
              </svg>
              Create your own (free)
              <br className="hidden md:block" /> art collection
            </li>
            <li className="flex items-center leading-tight">
              <svg
                width="40"
                height="42"
                viewBox="0 0 40 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-10"
              >
                <path
                  d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.2909 35.437 20.4632 35.4416"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M37.2134 33.0156V35.5547H25.1772V33.3818L30.9512 27.144C31.5534 26.4604 32.0295 25.8664 32.3794 25.3618C32.7293 24.8573 32.9775 24.4015 33.124 23.9946C33.2786 23.5796 33.356 23.1849 33.356 22.8105C33.356 22.2653 33.2583 21.7892 33.063 21.3823C32.8677 20.9673 32.5828 20.6458 32.2085 20.418C31.8423 20.1901 31.3906 20.0762 30.8535 20.0762C30.2594 20.0762 29.7508 20.2145 29.3276 20.4912C28.9126 20.7598 28.5952 21.1341 28.3755 21.6143C28.1639 22.0944 28.0581 22.6437 28.0581 23.2622H24.8232C24.8232 22.2124 25.0674 21.2562 25.5557 20.3936C26.0439 19.5228 26.7438 18.827 27.6553 18.3062C28.5667 17.7853 29.6491 17.5249 30.9023 17.5249C32.123 17.5249 33.1566 17.7284 34.0029 18.1353C34.8493 18.534 35.4922 19.1037 35.9316 19.8442C36.3711 20.5848 36.5908 21.4678 36.5908 22.4932C36.5908 23.0628 36.4972 23.6243 36.3101 24.1777C36.1229 24.7311 35.8584 25.2804 35.5166 25.8257C35.1829 26.3628 34.7842 26.908 34.3203 27.4614C33.8564 28.0148 33.3397 28.5804 32.77 29.1582L29.3032 33.0156H37.2134Z"
                  fill="white"
                />
              </svg>
              Our AI recommends
              <br /> art just for you
            </li>
            <li className="flex items-center leading-tight">
              <svg
                width="40"
                height="42"
                viewBox="0 0 40 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-10"
              >
                <path
                  d="M33.4352 12.9697C29.2987 5.80505 20.1373 3.35027 12.9727 7.48677C5.80808 11.6233 3.3533 20.7846 7.48979 27.9493C10.2619 32.7508 15.2909 35.437 20.4632 35.4416"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <path
                  d="M28.7051 25.2397H30.5239C31.1668 25.2397 31.6958 25.1299 32.1108 24.9102C32.534 24.6823 32.8473 24.373 33.0508 23.9824C33.2624 23.5837 33.3682 23.1239 33.3682 22.603C33.3682 22.0903 33.2746 21.6468 33.0874 21.2725C32.9002 20.89 32.6154 20.597 32.2329 20.3936C31.8586 20.182 31.3784 20.0762 30.7925 20.0762C30.3205 20.0762 29.8851 20.1698 29.4863 20.3569C29.0957 20.5441 28.7824 20.8127 28.5464 21.1626C28.3185 21.5044 28.2046 21.9154 28.2046 22.3955H24.9697C24.9697 21.4352 25.222 20.5929 25.7266 19.8687C26.2393 19.1362 26.931 18.5625 27.8018 18.1475C28.6725 17.7324 29.645 17.5249 30.7192 17.5249C31.8911 17.5249 32.9165 17.7202 33.7954 18.1108C34.6825 18.4933 35.3701 19.0589 35.8584 19.8076C36.3548 20.5563 36.603 21.484 36.603 22.5908C36.603 23.1198 36.4769 23.6488 36.2246 24.1777C35.9805 24.6986 35.6183 25.1706 35.1382 25.5938C34.6662 26.0088 34.0843 26.3465 33.3926 26.6069C32.709 26.8592 31.9237 26.9854 31.0366 26.9854H28.7051V25.2397ZM28.7051 27.73V26.0088H31.0366C32.0376 26.0088 32.9043 26.1268 33.6367 26.3628C34.3773 26.5907 34.9876 26.9162 35.4678 27.3394C35.9479 27.7544 36.3019 28.2386 36.5298 28.792C36.7658 29.3454 36.8838 29.9395 36.8838 30.5742C36.8838 31.4043 36.7292 32.1449 36.4199 32.7959C36.1107 33.4388 35.6753 33.984 35.1138 34.4316C34.5604 34.8792 33.9093 35.221 33.1606 35.457C32.4119 35.6849 31.6022 35.7988 30.7314 35.7988C29.9746 35.7988 29.2381 35.6971 28.522 35.4937C27.814 35.2821 27.1751 34.9688 26.6055 34.5537C26.0358 34.1305 25.5841 33.6016 25.2505 32.9668C24.9168 32.3239 24.75 31.5793 24.75 30.7329H27.9849C27.9849 31.2212 28.1029 31.6566 28.3389 32.0391C28.583 32.4215 28.9167 32.7227 29.3398 32.9424C29.7712 33.154 30.2554 33.2598 30.7925 33.2598C31.3866 33.2598 31.8952 33.154 32.3184 32.9424C32.7497 32.7227 33.0793 32.4134 33.3071 32.0146C33.535 31.6077 33.6489 31.1235 33.6489 30.562C33.6489 29.8947 33.5228 29.3535 33.2705 28.9385C33.0182 28.5234 32.6561 28.2183 32.1841 28.0229C31.7202 27.8276 31.1668 27.73 30.5239 27.73H28.7051Z"
                  fill="white"
                />
              </svg>
              Connect with fellow
              <br /> art curious people
            </li>
          </ul>
          <Button
            className="mt-20 rounded-10 border-4 border-white-100 px-30 py-15 text-20 font-bold text-white-100 transition-colors hover:bg-white-100 hover:text-new-blue-100 md:mt-40"
            onClick={handleBannerClick}
          >
            Find out
          </Button>
        </div>
      </div>
    </div>
  );
}

export default observer(PromoBanner);
